import { useEffect, useRef, useState } from "react";
import { Badge, Popover, PopoverContent, PopoverTrigger } from "@libs/zapi-ui/shadcn";
import { AppButton, AppInput, isEnterSubmit } from "@libs/zapi-ui/custom";
import { Check, ChevronDown, X } from "lucide-react";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import $api from "@/zapi-frontend/shared/api/axios";
import { tryCatch } from "@/zapi-frontend/shared/lib/tryCatch";
import { toast } from "sonner";
import { AxiosError } from "axios";
import { VersionHistoryPanel } from "./VersionHistoryPanel/VersionHistoryPanel";
import { useProjectVersionsStore } from "../model/projectVersionsStore";
import { displayVersionName } from "@/zapi-frontend/shared/i18n/versionDisplayName";

export type TProjectVersionItem = {
  id: string;
  name: string;
  date: string;
  lastModified?: string;
  modifiedBy?: string;
  chaptersGenerated?: number;
  chaptersTotal?: number;
  isGenerating?: boolean;
  framingPercent?: number;
  isDraft?: boolean;
};

type TProps = {
  projectId: string;
  projectName?: string;
  projectLogoUrl?: string | null;
  versionGroupId: string | null | undefined;
  versionGroupNewest: boolean | undefined;
  versions: TProjectVersionItem[];
  isDraft?: boolean;
  hasEdit: boolean;
  organizationId: number;
  showOlderCount?: boolean;
  selectedVersionId?: string;
  onSelectVersion?: (versionId: string) => void;
  onListInvalidated: () => void;
};

export function ProjectVersionBlock(props: TProps) {
  const {
    projectId,
    versionGroupId,
    versionGroupNewest,
    versions,
    hasEdit,
    organizationId,
    showOlderCount = true,
    selectedVersionId,
    onSelectVersion,
    onListInvalidated
  } = props;

  const isPopoverOpen = useProjectVersionsStore(
    (s) => versionGroupId != null && s.openPopoverVersionGroupId === versionGroupId
  );
  const setOpenPopoverVersionGroupId = useProjectVersionsStore((s) => s.setOpenPopoverVersionGroupId);
  const optimisticStartFirst = useProjectVersionsStore((s) => s.optimisticStartFirst);
  const onPopoverOpenChange = (open: boolean) =>
    setOpenPopoverVersionGroupId(open && versionGroupId != null ? versionGroupId : null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createValue, setCreateValue] = useState("");
  const [createPending, setCreatePending] = useState(false);
  const createFormRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (createOpen) createFormRef.current?.querySelector("input")?.focus();
  }, [createOpen]);

  if (versions.length === 0) {
    return (
      <span className="text-[14px] tabular-nums text-zinc-400" aria-hidden>
        —
      </span>
    );
  }

  if (versionGroupNewest === false) return null;

  const startFirstVersion = async () => {
    const trimmed = createValue.trim() || "Initial";
    setCreatePending(true);
    try {
      const [, err] = await tryCatch<unknown, AxiosError<{ message: string }>>(
        $api.post(`/projects/${projectId}/duplicate`, {
          organizationId,
          initialName: trimmed
        })
      );
      if (err) {
        toast.error(err.response?.data?.message || "Failed to start versioning");
        return;
      }
      toast.success("Version saved");
      optimisticStartFirst(projectId, trimmed);
      setCreateOpen(false);
      setCreateValue("");
      onListInvalidated();
    } finally {
      setCreatePending(false);
    }
  };

  const cancelCreate = () => {
    setCreateOpen(false);
    setCreateValue("");
  };

  if (!versionGroupId && !hasEdit) {
    return (
      <span className="block min-w-0 truncate text-[14px] text-zinc-600" data-prevent-row-nav>
        {versions[0]?.name}
      </span>
    );
  }

  if (!versionGroupId && hasEdit) {
    if (createOpen) {
      return (
        <div
          ref={createFormRef}
          className="flex min-w-0 max-w-full items-center gap-1"
          data-prevent-row-nav
          onClick={(e) => e.stopPropagation()}
        >
          <AppInput
            placeholder="New version..."
            value={createValue}
            onChange={(e) => setCreateValue(e.target.value)}
            onKeyDown={(e) => {
              if (isEnterSubmit(e, { allowShift: true })) {
                e.preventDefault();
                void startFirstVersion();
              } else if (e.key === "Escape") {
                e.preventDefault();
                cancelCreate();
              }
            }}
            containerClassName={cn("min-w-0 flex-1")}
            className="text-[14px]"
            disabled={createPending}
          />
          <AppButton
            type="button"
            size="icon"
            title="Save"
            className="size-8 shrink-0 rounded-lg bg-[#f4f4f5] text-[#18181b] hover:bg-[#ececee]"
            isLoading={createPending}
            loadingText=""
            onPointerDown={(e) => e.preventDefault()}
            onClick={() => void startFirstVersion()}
          >
            <Check className="h-4 w-4" />
          </AppButton>
          <AppButton
            type="button"
            size="icon"
            appearance="text"
            className="size-8 shrink-0"
            title="Cancel"
            disabled={createPending}
            onClick={(e) => {
              e.stopPropagation();
              cancelCreate();
            }}
          >
            <X className="h-4 w-4" />
          </AppButton>
        </div>
      );
    }
    return (
      <AppButton
        type="button"
        appearance="text"
        color="zinc950"
        size="sm"
        data-prevent-row-nav
        className="h-auto min-h-0 max-w-full justify-start truncate p-0 text-[12px] leading-[18px] font-normal text-zinc-500 shadow-none hover:bg-transparent hover:text-zinc-700"
        onClick={(e) => {
          e.stopPropagation();
          setCreateOpen(true);
        }}
      >
        Create Version +
      </AppButton>
    );
  }

  const active = versions.find((v) => v.id === selectedVersionId) ?? versions[0];
  const olderCount = versions.length - 1;

  const trigger = (
    <AppButton
      type="button"
      appearance="text"
      color="zinc950"
      size="sm"
      data-prevent-row-nav
      data-testid="version-trigger"
      className="h-auto min-h-0 max-w-full gap-1.5 p-0 text-left text-[14px] font-normal text-zinc-700 shadow-none hover:bg-transparent hover:text-zinc-900"
      onClick={(e) => e.stopPropagation()}
    >
      <span className="min-w-0 truncate">{displayVersionName(active?.name)}</span>
      {showOlderCount && olderCount > 0 ? (
        <Badge
          variant="secondary"
          className="shrink-0 border-0 bg-zinc-200 px-1.5 py-0 text-[10px] font-medium tabular-nums text-zinc-700 hover:bg-zinc-200"
        >
          +{olderCount}
        </Badge>
      ) : null}
      <ChevronDown
        className={cn("h-3.5 w-3.5 shrink-0 text-zinc-400 transition-transform", isPopoverOpen && "rotate-180")}
        aria-hidden
      />
    </AppButton>
  );

  return (
    <Popover open={isPopoverOpen} onOpenChange={onPopoverOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        forceMount
        align="start"
        collisionPadding={8}
        hideWhenDetached
        className="flex w-auto flex-col overflow-hidden border-0 p-0 shadow-none"
        style={{ maxHeight: "var(--radix-popover-content-available-height)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <VersionHistoryPanel
          selectedVersionId={selectedVersionId}
          onSelectVersion={onSelectVersion}
          rowProjectId={projectId}
          projectName={props.projectName}
          projectLogoUrl={props.projectLogoUrl}
          versions={versions}
          hasEdit={hasEdit}
          organizationId={organizationId}
          onInvalidated={onListInvalidated}
        />
      </PopoverContent>
    </Popover>
  );
}
